import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { usePersonalization } from "@/lib/personalization";
import { trackEvent } from "@/lib/analytics";
import ROUTES from "@/lib/howWeHelpRoutes.json";
import HowWeHelpDeepDive from "./HowWeHelpDeepDive";
import { pillarPath, PROFILE_TO_ROUTE } from "@/lib/seoArchitecture";

const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };
const ROUTE_LABELS_SEO = { work: "Work from Spain", retire: "Retire in Spain", home: "Buy a Home" };

export function useHowWeHelpContent() {
  const { profile } = usePersonalization();
  const { section } = useSiteContent();
  const c = section("how_we_help_routes", ROUTES);
  const route = profile || "neutral";
  const steps = (Array.isArray(c[`${route}_steps`]) && c[`${route}_steps`].length ? c[`${route}_steps`] : ROUTES[`${route}_steps`]).slice(0, 4);
  return { c, route, steps, profile };
}

// Editorial intro → four clickable route-specific steps → closing "Where we come in". Content: CMS `how_we_help_routes`.
export default function HowWeHelp() {
  const { c, route, steps, profile } = useHowWeHelpContent();
  const [active, setActive] = useState(null);
  const triggerRef = useRef(null);
  const { hash, key } = useLocation();

  useEffect(() => setActive(null), [route]);

  // Footer "How we help" links: /#how-we-help-step-N scrolls to the step (ScrollManager) and opens its deep dive.
  useEffect(() => {
    const m = /^#how-we-help-step-([1-4])$/.exec(hash || "");
    if (!m) return;
    const i = Number(m[1]) - 1;
    if (!steps[i]) return;
    const t = setTimeout(() => {
      triggerRef.current = document.querySelector(`[data-testid="help-step-${i + 1}"]`);
      trackEvent("how_we_help_deep_dive_open", { profile: route, step_id: steps[i].id, placement: "footer" });
      setActive(i);
    }, 450);
    return () => clearTimeout(t);
  }, [hash, key]); // eslint-disable-line react-hooks/exhaustive-deps

  const openStep = (step, i, el) => {
    triggerRef.current = el;
    trackEvent("how_we_help_step_click", { profile: route, step_id: step.id, placement: "home" });
    trackEvent("how_we_help_deep_dive_open", { profile: route, step_id: step.id, placement: "home" });
    setActive(i);
  };

  return (
    <section id="how-we-help" data-testid="how-we-help-section" data-route={route} className="bg-[#F5EFE7] text-[#451C25] py-24 md:py-32 px-6 md:px-10 lg:px-14">
      <div className="max-w-7xl mx-auto">
        <header data-testid="how-we-help-intro-block" className="max-w-3xl">
          <p className="text-[#897756] text-[11px] tracking-[0.3em] uppercase mb-6 font-semibold" style={K}>{c[`${route}_eyebrow`]}</p>
          <h2 data-testid="how-we-help-headline" className="text-4xl md:text-5xl leading-[1.05] tracking-tight" style={{ ...K, fontWeight: 600 }}>
            {c[`${route}_headline`]}
          </h2>
          <p data-testid="how-we-help-intro" className="mt-7 text-[#6B4954] text-base md:text-lg leading-relaxed max-w-2xl" style={S}>{c[`${route}_intro`]}</p>
        </header>

        <ol data-testid="how-we-help-areas" className="mt-14 md:mt-16 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-8 gap-y-12 md:gap-y-14">
          {steps.map((a, i) => (
            <li key={`${route}-${a.id || i}`} id={`how-we-help-step-${i + 1}`} data-testid={`help-area-${i + 1}`} className="min-w-0 scroll-mt-32">
              <button
                type="button"
                data-testid={`help-step-${i + 1}`}
                aria-haspopup="dialog"
                aria-expanded={active === i}
                onClick={(e) => openStep(a, i, e.currentTarget)}
                className="group w-full h-full text-left flex flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D] focus-visible:ring-offset-4 focus-visible:ring-offset-[#F5EFE7]"
              >
                <div className="relative overflow-hidden aspect-[4/3] w-full">
                  <img src={a.image} alt={a.alt || ""} loading={i < 2 ? "eager" : "lazy"} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100" />
                </div>
                <p className="mt-6 flex items-baseline gap-3">
                  <span className="text-[#B3A17D] text-[13px] tracking-[0.2em]" style={{ ...K, fontWeight: 500 }} aria-hidden="true">0{i + 1}</span>
                  <span className="h-px flex-1 bg-[#451C25]/15" aria-hidden="true" />
                </p>
                <h3 className="mt-3 text-xl md:text-[22px] leading-tight tracking-tight" style={{ ...K, fontWeight: 500 }}>{a.title}</h3>
                <p className="mt-3 text-[#6B4954] text-[15px] leading-relaxed flex-1">{a.short}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#451C25] font-semibold" style={K}>
                  <span className="relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-full after:bg-[#B3A17D] after:origin-left after:transition-transform after:duration-300 group-hover:after:scale-x-100 after:scale-x-[0.35] motion-reduce:after:transition-none">
                    {c.explore_label}
                  </span>
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transition-none" aria-hidden="true" />
                </span>
              </button>
            </li>
          ))}
        </ol>

        <div data-testid="how-we-help-closing-block" className="mt-12 md:mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-8 gap-y-4">
          <div className="xl:col-span-2 md:col-span-2 xl:pr-8">
            <p className="text-[#897756] text-[11px] tracking-[0.3em] uppercase font-semibold" style={K}>{c.help_label}</p>
            <p data-testid="how-we-help-closing" className="mt-3 text-[#451C25] text-xl md:text-2xl italic leading-snug max-w-md" style={S}>{c[`${route}_closing`]}</p>
          </div>
          {c.scope_note && (
            <div className="xl:col-span-2 md:col-span-2 md:mt-[34px]">
              <p data-testid="how-we-help-scope" className="text-[#6B4954] text-[14px] md:text-[15px] leading-relaxed" style={S}>{c.scope_note}</p>
              <p data-testid="how-we-help-seo-links" className="mt-4 text-[13px] text-[#6B4954]" style={S}>
                {profile && PROFILE_TO_ROUTE[profile] ? <>Read the full <a href={pillarPath("spain", PROFILE_TO_ROUTE[profile])} className="text-[#451C25] underline underline-offset-4 decoration-[#B3A17D]">{ROUTE_LABELS_SEO[PROFILE_TO_ROUTE[profile]]}</a> overview · </> : null}
                Planning a move to Spain? <a href={pillarPath("spain", "general")} className="text-[#451C25] underline underline-offset-4 decoration-[#B3A17D]">Start with the overview</a>.
              </p>
            </div>
          )}
        </div>
      </div>

      <HowWeHelpDeepDive open={active !== null} onOpenChange={(o) => !o && setActive(null)} returnFocusTo={triggerRef} step={active !== null ? steps[active] : null} index={active ?? 0} profile={profile} c={c} />
    </section>
  );
}
